/**
 * Copyright (c) 2023-present,
 * Eunoia Digital, S.L. All rights reserved.
 */

import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { Image, TemplateText, Text } from "src/presentation/components/atoms";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { CountryLocationService } from "@services/country-location-service";
import { settings } from "@config/settings";
import { localesParserDictionary } from "@utils/locales-parser";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { MainRouter } from "@config/urls";
import ArrowDownIcon from "@assets/icons/arrow-down.svg";
import ArrowUpIcon from "@assets/icons/arrow-up.svg";
import styles from "./producto-detalle-page.module.scss";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import classNames from "classnames";
import { NextSeo } from "next-seo";
import { ProductosService } from "@services/productos-service";
import { ProductoDetalle } from "@models/producto-detalle";
import { EnsayoThumbnail } from "src/presentation/components/organisms/ensayo-thumbnail";
import { AnalitycsService } from "@services/analytics-service";
import ProductoNoEncontrado from "../../../producto-no-encontrado";
import {
  ContactForm,
  ProductCard,
} from "src/presentation/components/organisms";
import Link from "next/link";
import { useRouter } from "next/router";
import Cookies from "js-cookie";

const ProductoDetallePage: NextPage = (props: any) => {
  const {
    productoDetalle,
    country,
    paisDeVenta,
    productodePrueba,
    productoNombrePaisCorrecto,
    internacional,
  } = props;
  const formRef = useRef(null);
  const { t } = useTranslation();
  const router = useRouter();
  const { lang } = router.query;

  const [productData, setProductData] = useState(
    new ProductoDetalle().deserialize(JSON.parse(productoDetalle))
  );
  const [contact, setContact] = useState<boolean>(false);
  const [hasMounted, sethasMounted] = useState(false);

  useEffect(() => {
    sethasMounted(true);
  }, []);

  useEffect(() => {
    setProductData(
      new ProductoDetalle().deserialize(JSON.parse(productoDetalle))
    );
  }, [productoDetalle, lang]);

  const toggleContact = () => {
    if (!contact) {
      new AnalitycsService().trackEvent({
        category: "Formulario",
        action: "SolicitarInformacion",
        label: productData.nombre,
      });
    }
    setContact(!contact);
  };
  const handleValueChange = (
    paisDeVenta: any,
    producto: string,
    internacional: boolean
  ) => {
    Cookies.set(settings.countryCookie, paisDeVenta.toLowerCase(), {
      expires: 7,
      path: "/",
    });
    router.push(
      internacional
        ? `/${paisDeVenta.toLowerCase()}/productos/internacional/${productodePrueba}`
        : `/${paisDeVenta.toLowerCase()}/productos/${productodePrueba}`
    );
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      left: 0,
      behavior: "smooth",
    });
  };
  const ProductDetailSection = () => {
    return (
      <section className={styles.productDetailSection}>
        <div className={styles.left}>
          <Image
            src={productData.imagen_catalogo}
            alt={
              productData.seo_imagen_catalogo_alt === ""
                ? "manvert " + productData.nombre
                : productData.seo_imagen_catalogo_alt
            }
          />
          {productData.ecologico === "si" ? (
            <Image
              src={"/static/images/eco.webp"}
              alt="manvert eco"
              className={styles.eco}
            />
          ) : null}
        </div>
        <div className={styles.right}>
          <div className={styles.data}>
            {paisDeVenta !== null && productodePrueba !== null ? (
              lang?.toString().toUpperCase() !== paisDeVenta.toUpperCase() ? (
                <div>
                  <div
                    onClick={() => {
                      handleValueChange(
                        paisDeVenta,
                        productodePrueba,
                        internacional
                      );
                    }}
                  >
                    <div className={styles.cardAdvice}>
                      {t("ProductDetailView.cardAdvice")}
                      <div className={styles.linkProducto}>
                        <Link
                          href={
                            internacional
                              ? MainRouter[
                                  "producto-detalle-internacional"
                                ].generatePath({
                                  id: productodePrueba,
                                  country: paisDeVenta,
                                })
                              : MainRouter["producto-detalle"].generatePath({
                                  id: productodePrueba,
                                  country: paisDeVenta,
                                })
                          }
                        >
                          {productoNombrePaisCorrecto}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null
            ) : paisDeVenta !== null && productodePrueba === null ? (
              <div>
                <div
                  onClick={() => {
                    scrollToBottom();
                  }}
                >
                  <div className={styles.cardAdvice}>
                    {t("ProductDetailView.enlaceFooter")}
                    <div className={styles.linkProductoAqui}>
                      <div>
                        {t("ProductDetailView.enlaceFooterAqui").toLowerCase()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <Text as="h1">{productData.nombre}</Text>
            <TemplateText template="small">
              {productData.descripcion}
            </TemplateText>

            <div className={styles.productLinks}>
              <button
                onClick={() => toggleContact()}
                className={styles.solicitarButton}
                type="button"
              >
                {t("ProductDetailView.solicitar")}
                {contact ? <ArrowUpIcon /> : <ArrowDownIcon />}
              </button>
              {(() => {
                const dest = settings.PRODUCTOS_DESTACADOS.find(
                  (x) => x.id === productData.id_producto_generico
                );
                if (dest) {
                  return (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={dest.url}
                    >
                      {t("ProductDetailView.saber")}
                    </a>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const ContactSection = () => {
    return (
      <section
        ref={formRef}
        style={{
          display: contact ? "block" : "none",
        }}
        className={styles.contactSection}
      >
        <ContactForm
          title={`${t("ContactForm.productTitle")} ${
            productData ? productData.nombre : ""
          }`}
          empresa
          cultivos
          documentos
          actividad
          card
          telefono
          ciudad
          provincia
          comentarios
          link="https://go.pardot.com/l/939633/2021-09-08/fpjt73"
          GDPRTextVersion="producto"
          eventLabel={productData ? productData.nombre : ""}
          eventAction="EnviarSolicitud"
        />
      </section>
    );
  };

  const GruposCultivoSection = () => {
    return productData.grupos_de_cultivo.length > 0 ? (
      <section className={styles.grupoCultivoSection}>
        <Text as="h2">{t("ProductDetailView.cultivo")}</Text>
        <div className={styles.cultivoscontainer}>
          {productData.grupos_de_cultivo.map((item) => {
            return (
              <TemplateText
                key={item}
                template="normal"
                className={classNames(styles.wrapper)}
              >
                {item}
              </TemplateText>
            );
          })}
        </div>
      </section>
    ) : null;
  };

  const SolucionesSection = () => {
    return (
      <section className={styles.solucionesSection}>
        <Text as="h2">{t("ProductDetailView.soluciones")}</Text>
        <div className={styles.solucionescontainer}>
          {productData.soluciones.map((item) => {
            return <span key={item}>{item}</span>;
          })}
          {productData.soluciones.length === 0 && (
            <TemplateText template="normal">
              {t("ProductDetailView.nosoluciones")}
            </TemplateText>
          )}
        </div>
      </section>
    );
  };

  const EnsayosSection = () => {
    return (
      <section className={styles.ensayosSection}>
        {productData.ensayos.length > 0 ? (
          <Text as="h2">{t("ProductDetailView.ensayos")}</Text>
        ) : null}

        <div className={styles.ensayosContainer}>
          {productData.ensayos.map((item) => {
            return (
              <div key={item.slug} className={styles.wrapper}>
                <EnsayoThumbnail
                  slug={item.slug}
                  id={item.slug}
                  title={item.nombre}
                  image={item.imagen}
                  country={country}
                  subtitle={`${t("ProductDetailView.realizado")} ${item.pais}`}
                />
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const RelacionadosSection = () => {
    return (
      <section className={styles.relacionadosSection}>
        {productData.productos_relacionados &&
          productData.productos_relacionados.length > 0 && (
            <Text as="h2" className={styles.title}>
              {t("ProductDetailView.productos")}
            </Text>
          )}

        <div className={styles.productsContainer}>
          {productData &&
            productData.productos_relacionados.slice(0, 3).map((item: any) => {
              return (
                <div key={item.slug} className={styles.wrapper}>
                  <ProductCard
                    slug={item.slug}
                    name={item.nombre}
                    internacional={item.internacional}
                    eco={item.ecologico === "si"}
                    category={item.familia.nombre}
                    categoryColor={item.familia.color}
                    image={item.imagen_catalogo}
                    descripcion={[item.descripcion2]}
                    animated
                    country={country}
                    card
                  />
                </div>
              );
            })}
        </div>
      </section>
    );
  };

  return (
    <>
      {hasMounted && (
        <>
          {" "}
          <NextSeo
            title={
              productData.seo_title === ""
                ? "manvert | " + productData.nombre
                : productData.seo_title
            }
            description={`${
              productData.seo_description === ""
                ? productData.descripcion2
                : productData.seo_description
            }`}
          />
          {productData.slug === "" ? (
            <ProductoNoEncontrado />
          ) : (
            <MainLayout
              breadcrumb={[
                {
                  label: t("urls.home"),
                  url: MainRouter.home.generatePath(),
                },
                {
                  label: t("urls.productos"),
                  url: `/${country}${MainRouter.productos.generatePath()}`,
                },
                {
                  label: productData.nombre,
                  url: MainRouter.productos.generatePath(),
                },
              ]}
            >
              <div className={styles.root}>
                <ProductDetailSection />
                <ContactSection />
                <GruposCultivoSection />
                <SolucionesSection />
                <EnsayosSection />
                <RelacionadosSection />
              </div>
            </MainLayout>
          )}
        </>
      )}
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, params, query, res } = Props;
  const productoId = params?.id as string;
  const host = `https://${req.headers.host}`;
  const { country } = query;
  let productoFiltradoPaisVenta;
  let productoFiltrado: any;
  let internacional = false;

  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country:
      country === undefined ? selectedCountry.countrycode : country.toString(),
  };

  const result = await new ProductosService().getProductoDetalle({
    ...opts,
    slug: productoId,
  });

  if (selectedCountry.countrycode !== country?.toString()) {
    const opts = {
      locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
      country: selectedCountry.countrycode,
    };
    const entities = await new EntitiesFetcherService().getEntities({
      ...opts,
      productos: true,
      productosDestacados: true,
    });

    productoFiltradoPaisVenta = selectedCountry.countrycode;
    productoFiltrado = entities.productosData?.find(
      (x) => x.producto_generico === result.id_producto_generico
    );
  }
  if (productoFiltrado?.internacional === true) {
    const productosGenericosArray =
      await new ProductosService().getProductosGenericos({
        ...opts,
      });
    if (productoFiltrado) {
      const informacionEncontrada = productosGenericosArray.find(
        (pg) => pg.id === productoFiltrado?.producto_generico
      );
      productoFiltrado = informacionEncontrada;
      internacional = true;
    }
  }
  if (!result || result.slug === "") {
    return {
      redirect: {
        permanent: true,
        destination: MainRouter["producto-no-encontrado"].generatePath(),
      },
    };
  }

  return {
    props: {
      paisDeVenta: productoFiltradoPaisVenta || null,
      productodePrueba: productoFiltrado?.slug || null,
      productoNombrePaisCorrecto: productoFiltrado?.nombre || null,
      paisQuery: country || undefined,
      host: host,
      internacional: internacional,
      country: selectedCountry.countrycode,
      productoDetalle: JSON.stringify(result),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default ProductoDetallePage;
